import React, { useState } from 'react';
import { StockDto } from 'src/services/stock';
import { StockToApply } from './utils';

export type StockModalProps = {
  isOpen: boolean;
  onClose: () => void;
  stockData: StockDto[] | null;
  onApplyStock: (stock: StockToApply) => void;
  bucketUrl: string;
};

export const StockModal = ({
  isOpen,
  onClose,
  stockData,
  onApplyStock,
  bucketUrl,
}: StockModalProps) => {
  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm'
      onClick={onClose}
    >
      <div
        className='w-[70%] h-[70%] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='bg-gradient-to-r from-turquoise-dark to-turquoise px-8 py-5 flex justify-between items-center shrink-0'>
          <div>
            <h1 className='text-2xl font-bold text-white tracking-tight'>
              Stocks disponibles
            </h1>
            <p className='mt-2 text-sm text-white/70'>
              {stockData?.length || 0} stock
              {(stockData?.length || 0) > 1 ? 's' : ''} trouvé
              {(stockData?.length || 0) > 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className='w-12 h-12 rounded-xl bg-white/10 border border-white/20 text-white text-2xl flex items-center justify-center cursor-pointer transition-all hover:bg-white/20'
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-10 bg-grey-light'>
          {stockData && stockData.length > 0 ? (
            <div className='flex flex-col gap-6'>
              {stockData.map((stock, index) => (
                <StockCard
                  key={stock.id || index}
                  stock={stock}
                  onApply={() => onApplyStock(stock)}
                  bucketUrl={bucketUrl}
                />
              ))}
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </div>
  );
};

const getUniqueGranitNames = (stock: StockDto): string[] => {
  const names: string[] = [];
  stock.stockPieces?.forEach((p) => {
    if (p.granit?.name) names.push(p.granit.name);
  });
  stock.stockSteles?.forEach((s) => {
    if (s.granit?.name) names.push(s.granit.name);
  });
  stock.stockPlatings?.forEach((p) => {
    if (p.granit?.name) names.push(p.granit.name);
  });
  stock.stockSoles?.forEach((s) => {
    if (s.granit?.name) names.push(s.granit.name);
  });
  return [...new Set(names)];
};

type StockCardProps = {
  stock: StockDto;
  onApply: () => void;
  bucketUrl: string;
};

const StockCard = ({ stock, onApply, bucketUrl }: StockCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const granitNames = getUniqueGranitNames(stock);

  return (
    <div
      className='bg-white rounded-xl shadow-md overflow-hidden border border-slate-200 transition-all hover:shadow-lg hover:border-turquoise group'
    >
      {/* Card Header avec prix intégré */}
      <div className='bg-turquoise px-4 py-3 flex justify-between items-center group-hover:bg-turquoise-dark transition-colors'>
        <div className='flex flex-col min-w-0 flex-1'>
          <h2 className='text-base font-semibold text-white'>{stock.name}</h2>
          {granitNames.length > 0 && (
            <p className='text-xs text-white/70 truncate mt-0.5'>
              {granitNames.join(' • ')}
            </p>
          )}
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
            className='w-8 h-8 rounded-lg bg-white/10 border border-white/20 text-white text-sm flex items-center justify-center cursor-pointer transition-all hover:bg-white/20'
            title={isExpanded ? 'Replier' : 'Déplier'}
          >
            <span
              className={`inline-block transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            >
              ›
            </span>
          </button>
          <button
            onClick={onApply}
            className='bg-white/20 px-3 py-1.5 rounded text-xs font-medium text-white cursor-pointer transition-all hover:bg-white/30'
          >
            Appliquer
          </button>
        </div>
      </div>

      {/* Card Body - Collapsible */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className='p-4'>
          {/* Grille compacte des éléments */}
          <div className='grid grid-cols-2 gap-2'>
            {/* Pieces */}
            {stock.stockPieces &&
              stock.stockPieces.length > 0 &&
              stock.stockPieces.map((piece: any, pIndex) => (
                <StockItemRow
                  key={`piece-${pIndex}`}
                  bgColor='bg-turquoise-light'
                  dotColor='bg-turquoise'
                  textColor='text-turquoise-dark'
                  subTextColor='text-turquoise'
                  borderColor='border-turquoise/30'
                  title={piece.piece?.name || `Pièce #${piece.pieceId}`}
                  subtitle={`${piece.granit?.name || ''} ${
                    piece.x && piece.y && piece.z
                      ? `• ${Math.round(piece.x)}×${Math.round(
                          piece.y,
                        )}×${Math.round(piece.z)}`
                      : ''
                  }`}
                  imageUrl={
                    piece.granit?.image
                      ? `${bucketUrl}/${piece.granit.image}`
                      : undefined
                  }
                />
              ))}

            {/* Steles */}
            {stock.stockSteles &&
              stock.stockSteles.length > 0 &&
              stock.stockSteles.map((stele, sIndex) => (
                <StockItemRow
                  key={`stele-${sIndex}`}
                  bgColor='bg-gold-light'
                  dotColor='bg-gold'
                  textColor='text-amber-700'
                  subTextColor='text-gold'
                  borderColor='border-gold/30'
                  title={stele.granit?.name || 'Stèle'}
                  subtitle={
                    stele.boxHeight && stele.boxUpWidth && stele.depth
                      ? `H${stele.boxHeight}×L${stele.boxUpWidth}×P${stele.depth}`
                      : ''
                  }
                  imageUrl={
                    (stele.granit as any)?.image
                      ? `${bucketUrl}/${(stele.granit as any).image}`
                      : undefined
                  }
                />
              ))}

            {/* Platings */}
            {stock.stockPlatings &&
              stock.stockPlatings.length > 0 &&
              stock.stockPlatings.map((plating, plIndex) => (
                <StockItemRow
                  key={`plating-${plIndex}`}
                  bgColor='bg-turquoise-dark/10'
                  dotColor='bg-turquoise-dark'
                  textColor='text-turquoise-dark'
                  subTextColor='text-turquoise'
                  borderColor='border-turquoise-dark/30'
                  title={plating.granit?.name || 'Placage'}
                  subtitle={
                    plating.length && plating.depth
                      ? `${plating.length}×${plating.depth} cm`
                      : ''
                  }
                  imageUrl={
                    (plating.granit as any)?.image
                      ? `${bucketUrl}/${(plating.granit as any).image}`
                      : undefined
                  }
                />
              ))}

            {/* Soles */}
            {stock.stockSoles &&
              stock.stockSoles.length > 0 &&
              stock.stockSoles.map((sole, soIndex) => (
                <StockItemRow
                  key={`sole-${soIndex}`}
                  bgColor='bg-red-light'
                  dotColor='bg-red'
                  textColor='text-red'
                  subTextColor='text-red'
                  borderColor='border-red/30'
                  title={sole.granit?.name || 'Semelle'}
                  subtitle={
                    sole.length && sole.depth && sole.height
                      ? `${sole.length}×${sole.depth}×${sole.height} cm`
                      : ''
                  }
                  imageUrl={
                    (sole.granit as any)?.image
                      ? `${bucketUrl}/${(sole.granit as any).image}`
                      : undefined
                  }
                />
              ))}
          </div>

          {/* Footer avec badge et indication */}
          <div className='mt-3 pt-3 border-t border-grey/10 flex justify-between items-center'>
            {stock.isAssembly ? (
              <span className='bg-turquoise-light text-turquoise px-2 py-0.5 rounded text-[10px] font-semibold'>
                Assemblage complet
              </span>
            ) : (
              <span />
            )}
            <span className='text-[10px] text-gray-400 group-hover:text-turquoise transition-colors'>
              Cliquez sur "Appliquer" pour utiliser ce stock
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

type StockItemRowProps = {
  bgColor: string;
  dotColor: string;
  textColor: string;
  subTextColor: string;
  borderColor: string;
  title: string;
  subtitle: string;
  imageUrl?: string;
};

const StockItemRow = ({
  bgColor,
  dotColor,
  textColor,
  subTextColor,
  borderColor,
  title,
  subtitle,
  imageUrl,
}: StockItemRowProps) => {
  return (
    <div className={`${bgColor} px-3 py-2 rounded-md flex items-center gap-2`}>
      <span className={`w-1.5 h-1.5 ${dotColor} rounded-full shrink-0`} />
      <div className='flex-1 min-w-0'>
        <p className={`text-xs font-semibold ${textColor} truncate`}>{title}</p>
        <p className={`text-[10px] ${subTextColor} truncate`}>{subtitle}</p>
      </div>
      {imageUrl && (
        <img
          src={imageUrl}
          alt={title}
          className={`w-8 h-8 rounded-md object-cover shrink-0 border ${borderColor}`}
        />
      )}
    </div>
  );
};

const EmptyState = () => {
  return (
    <div className='flex flex-col items-center justify-center h-full min-h-[400px]'>
      <div className='w-30 h-30 bg-turquoise-light rounded-full flex items-center justify-center mb-6'>
        <span className='text-5xl'>📦</span>
      </div>
      <h2 className='text-2xl font-semibold text-grey'>
        Aucun stock disponible
      </h2>
      <p className='mt-3 text-base text-gray-400'>
        Il n'y a pas de stock correspondant à ce monument
      </p>
    </div>
  );
};
